<template>
  <div class="container-fluid px-4">
    <div class="row">
      <div class="col-12">
        <div class="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center pt-3 pb-2 mb-3 border-bottom">
          <h1 class="h2">{{ $t('password.change') }}</h1>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-md-6">
        <div class="card">
          <div class="card-header">
            <h5 class="card-title mb-0">{{ $t('password.change') }}</h5>
          </div>
          <div class="card-body">
            <form @submit.prevent="submitChangePassword">
              <div class="mb-3">
                <label for="currentPassword" class="form-label">{{ $t('password.current') }}</label>
                <div class="password-input-wrapper">
                  <input
                    :type="showCurrentPassword ? 'text' : 'password'"
                    class="form-control"
                    :class="{ 'is-invalid': errors.current_password }"
                    id="currentPassword"
                    v-model="form.current_password"
                    @invalid="handleInvalidCurrentPassword"
                    @input="clearCurrentPasswordError"
                  >
                  <button
                    type="button"
                    class="btn-password-toggle"
                    @click="showCurrentPassword = !showCurrentPassword"
                    tabindex="-1"
                  >
                    <i :class="showCurrentPassword ? 'bi bi-eye' : 'bi bi-eye-slash'"></i>
                  </button>
                </div>
                <div v-if="errors.current_password" class="invalid-feedback">
                  {{ errors.current_password[0] }}
                </div>
              </div>

              <div class="mb-3">
                <label for="newPassword" class="form-label">{{ $t('password.new') }}</label>
                <div class="password-input-wrapper">
                  <input
                    :type="showNewPassword ? 'text' : 'password'"
                    class="form-control"
                    :class="{ 'is-invalid': errors.password }"
                    id="newPassword"
                    v-model="form.password"
                    @invalid="handleInvalidPassword"
                    @input="clearPasswordError"
                  >
                  <button
                    type="button"
                    class="btn-password-toggle"
                    @click="showNewPassword = !showNewPassword"
                    tabindex="-1"
                  >
                    <i :class="showNewPassword ? 'bi bi-eye' : 'bi bi-eye-slash'"></i>
                  </button>
                </div>
                <div v-if="errors.password" class="invalid-feedback">
                  {{ errors.password[0] }}
                </div>
                <small class="form-text text-muted">{{ $t('password.minLength') }}</small>
              </div>

              <div class="mb-3">
                <label for="confirmPassword" class="form-label">{{ $t('password.confirm') }}</label>
                <div class="password-input-wrapper">
                  <input
                    :type="showConfirmPassword ? 'text' : 'password'"
                    class="form-control"
                    :class="{ 'is-invalid': errors.password_confirmation }"
                    id="confirmPassword"
                    v-model="form.password_confirmation"
                    @invalid="handleInvalidConfirmPassword"
                    @input="clearConfirmPasswordError"
                  >
                  <button
                    type="button"
                    class="btn-password-toggle"
                    @click="showConfirmPassword = !showConfirmPassword"
                    tabindex="-1"
                  >
                    <i :class="showConfirmPassword ? 'bi bi-eye' : 'bi bi-eye-slash'"></i>
                  </button>
                </div>
                <div v-if="errors.password_confirmation" class="invalid-feedback">
                  {{ errors.password_confirmation[0] }}
                </div>
              </div>

              <div class="d-flex gap-2">
                <button type="submit" class="btn btn-primary" :disabled="loading">
                  <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
                  {{ $t('password.button') }}
                </button>
                <button type="button" class="btn btn-secondary" @click="goBack">
                  {{ $t('password.cancel') }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { changePassword } from '../../api/admin/AuthApi.js';

const router = useRouter();
const { t } = useI18n();

const loading = ref(false);
const errors = ref({});
const showCurrentPassword = ref(false);
const showNewPassword = ref(false);
const showConfirmPassword = ref(false);

const form = reactive({
  current_password: '',
  password: '',
  password_confirmation: ''
});

/**
 * Change password function
 */
const submitChangePassword = async () => {
  // Custom validations
  if (!form.current_password) {
    alert(t('validation.required.currentPassword'));
    return;
  }
  
  if (!form.password) {
    alert(t('validation.required.newPassword'));
    return;
  }
  
  if (form.password.length < 8) {
    alert(t('validation.minLength'));
    return;
  }
  
  if (!form.password_confirmation) {
    alert(t('validation.required.confirmPassword'));
    return;
  }
  
  if (form.password !== form.password_confirmation) {
    alert(t('validation.mismatch'));
    return;
  }

  loading.value = true;
  errors.value = {};

  try {
    const response = await changePassword(
      form.current_password,
      form.password,
      form.password_confirmation
    );
    
    alert(t('password.success'));
    
    // Clear form
    form.current_password = '';
    form.password = '';
    form.password_confirmation = '';
    
    // Back to admin home
    router.push({ name: 'admin.home' });
    
  } catch (error) {
    if (error.status === 422) {
      errors.value = error.errors;
      
      // Show alert for server validation errors
      if (error.errors.current_password) {
        alert(error.errors.current_password[0]);
      } else if (error.errors.password) {
        alert(error.errors.password[0]);
      } else if (error.errors.password_confirmation) {
        alert(error.errors.password_confirmation[0]);
      }
    } else {
      alert(t('validation.error'));
    }
  } finally {
    loading.value = false;
  }
};

/**
 * Back to previous page
 */
const goBack = () => {
  router.go(-1);
};

/**
 * Event handler for invalid current password input
 */
const handleInvalidCurrentPassword = (event) => {
  event.preventDefault();
  alert(t('validation.required.currentPassword'));
};

/**
 * Event handler for invalid new password input
 */
const handleInvalidPassword = (event) => {
  event.preventDefault();
  if (form.password.length === 0) {
    alert(t('validation.required.newPassword'));
  } else if (form.password.length < 8) {
    alert(t('validation.minLength'));
  }
};

/**
 * Event handler for invalid confirm password input
 */
const handleInvalidConfirmPassword = (event) => {
  event.preventDefault();
  alert(t('validation.required.confirmPassword'));
};

/**
 * Functions to clear specific field errors
 */
const clearCurrentPasswordError = () => {
  if (errors.value.current_password) {
    delete errors.value.current_password;
  }
};

const clearPasswordError = () => {
  if (errors.value.password) {
    delete errors.value.password;
  }
};

const clearConfirmPasswordError = () => {
  if (errors.value.password_confirmation) {
    delete errors.value.password_confirmation;
  }
};
</script>

<style scoped>
.card {
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  border: 1px solid rgba(0, 0, 0, 0.125);
}

.form-control:focus {
  border-color: #86b7fe;
  outline: 0;
  box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
}

.password-input-wrapper {
  position: relative;
}

.password-input-wrapper .form-control {
  padding-right: 2.5rem;
}

.btn-password-toggle {
  position: absolute;
  right: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #6c757d;
  cursor: pointer;
  padding: 0.25rem 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-password-toggle:hover {
  color: #495057;
}

.btn-password-toggle:focus {
  outline: none;
}

.btn-password-toggle i {
  font-size: 1.1rem;
}
</style>